import React, { useState, useCallback, useEffect, useMemo } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import * as Icons from "react-feather";
import { useSelector } from "react-redux";
import { useAppThunkDispatch } from "redux/store";
import { Avatar } from "component/shared/avatar";
import { Sort } from "component/shared/table";
import { RefreshTable } from "component/shared/refresh-table";
import { Agent } from "model/agent";
import { numberedDateFormatter } from "shared/date-formatter";
import { AgentColumnType } from "type/agent-column-type";
import { styles } from "./style";
import { AddMemberDialog } from "../add-member-dialog";
import { RouterLinkWrapper } from "component/shared/router-link-wrapper";
import { getGeographicRoutes, getLeadsOptimized, getRoundRobinRoutes, getUser, getAgentTableColumns, getOffices } from "redux/selector";
import { updateAgentPageColumns } from "redux/slice/agent-page";
import moment from "moment";
import { useLocation } from "react-router-dom";
import queryString from "query-string";
import * as env from "shared/env";
import { useTheme } from "@mui/material/styles";
import { withMediaQuery, WithMediaQuery } from "component/shared/with-media-query";
import { getMemberDisplayName, getMemberUrl, getRoleTags, getRoutingTags, isBillableFilterParam, matchesBillableFilter, ROLE_TAG_LABELS } from "./member-meta";
import { TeamToolbar, TeamFilters, SortOption, OfficeOption } from "./toolbar";
import { MemberCard } from "./member-card";
import { Tag } from "component/shared/tag";
import { useColumnPrefs } from "../use-column-prefs";

interface Props extends OwnProps, WithStyles<typeof styles>, WithMediaQuery {}

interface OwnProps {
	agents: Agent[];
	maxForTeams?: boolean;
}

// Sort options per Figma. The design lists "Number of Leads" and "Leads" separately, but both
// sort the same lead-count column, so the redundant "Leads" entry is omitted.
const SORT_OPTIONS: SortOption[] = [
	{ id: "label", label: "Member", desc: false },
	{ id: "role", label: "Role", desc: false },
	{ id: "leads", label: "Number of Leads", desc: true },
	{ id: "lastLogin", label: "Last Login", desc: true },
	{ id: "activeListings", label: "Active Listings", desc: true },
];

const DEFAULT_SORT: Sort = { id: "label", desc: false };

const AgentTableComponent: React.FC<Props> = ({ agents, mediaQuery: isMobile, classes = {} }) => {
	const theme = useTheme();
	const dispatch = useAppThunkDispatch();

	// Redux selectors
	const columnTypesValues = useSelector(getAgentTableColumns);
	const user = useSelector(getUser);
	const leads = useSelector(getLeadsOptimized);
	const roundRobinRoutes = useSelector(getRoundRobinRoutes);
	const geographicRoutingRoutes = useSelector(getGeographicRoutes);

	const { readColumnPrefs, writeColumnPrefs } = useColumnPrefs(user.agentId);

	const defaultColumns = useMemo<AgentColumnType[]>(() => [
		AgentColumnType.AVATAR,
		AgentColumnType.LABEL,
		AgentColumnType.ID,
		AgentColumnType.ROLE,
		AgentColumnType.BILLABLE_USER,
		AgentColumnType.LEADS,
		AgentColumnType.ACTIVE_LISTINGS,
		AgentColumnType.OFFICE,
		AgentColumnType.ROUTING,
		AgentColumnType.LAST_LOGIN,
		AgentColumnType.ACTION,
	], []);

	// Hydrate column visibility from localStorage (falls back to all columns) on mount.
	// readColumnPrefs returns null when there is no valid saved selection, so the fallback
	// to defaultColumns covers both "never saved" and "saved ids all invalid".
	useEffect(() => {
		if (!columnTypesValues.length) {
			const persistedIds = readColumnPrefs();
			const columns = persistedIds
				? persistedIds.map((id) => AgentColumnType.getById(id)).filter(Boolean) as AgentColumnType[]
				: defaultColumns;
			dispatch(updateAgentPageColumns(columns));
		}
	}, [columnTypesValues.length, defaultColumns, readColumnPrefs, dispatch]);

	const handleColumnsChange = useCallback((columns: AgentColumnType[]) => {
		dispatch(updateAgentPageColumns(columns));
		writeColumnPrefs(columns);
	}, [dispatch, writeColumnPrefs]);

	// Local UI state
	const [addAgentDialogIsOpen, setAddAgentDialogIsOpen] = useState(false);
	const [term, setTerm] = useState("");
	const [sort, setSort] = useState<Sort>(DEFAULT_SORT);
	// "View Office Members" on the Office detail view links here with ?officeId=<id>, so the
	// office filter has to be seeded from the URL rather than starting empty (MAX-4065).
	const location = useLocation();
	// Lazy useState initializer, so the URL is read once on mount and later filter edits are never
	// reverted by it. Deliberately not an effect — that would clobber a user's own selection.
	const [filters, setFilters] = useState<TeamFilters>(() => {
		const query = queryString.parse(location.search);
		const raw = query.officeId;
		const offices = (Array.isArray(raw) ? raw : [raw])
			.map((value) => parseInt(String(value), 10))
			.filter((value) => !isNaN(value));
		return { roles: [], routing: [], offices, billable: env.billingRefresh && isBillableFilterParam(query.billable) };
	});

	const offices = useSelector(getOffices);
	const officeOptions = useMemo<OfficeOption[]>(
		() =>
			[...offices]
				.sort((a, b) => (a.name || "").localeCompare(b.name || ""))
				.map((office) => ({ id: office.id, label: office.name || `Office ${office.id}` })),
		[offices]
	);

	// If the column being sorted is hidden, the table can no longer apply that sort — fall back to
	// the default so the toolbar's Sort selection never disagrees with the actual row order.
	useEffect(() => {
		const sortColumnType = AgentColumnType.getById(sort.id);
		if (sortColumnType && sort.id !== DEFAULT_SORT.id && !columnTypesValues.includes(sortColumnType)) {
			setSort(DEFAULT_SORT);
		}
	}, [columnTypesValues, sort.id]);

	const showAddAgentDialog = useCallback(() => setAddAgentDialogIsOpen(true), []);
	const hideAddAgentDialog = useCallback(() => setAddAgentDialogIsOpen(false), []);

	// Precompute leads-per-agent once (O(leads)) rather than scanning the leads array per
	// agent on every filter/sort/render pass (which would be O(agents × leads)).
	const leadCountByAgentId = useMemo(() => {
		const counts = new Map<number, number>();
		leads.forEach((lead) => {
			const agentId = lead.agent?.id;
			if (agentId != null) {
				counts.set(agentId, (counts.get(agentId) ?? 0) + 1);
			}
		});
		return counts;
	}, [leads]);

	const leadCountFor = useCallback((agent: Agent) => {
		return agent.ownsLeads ? (leadCountByAgentId.get(agent.id) ?? 0) : 0;
	}, [leadCountByAgentId]);

	const billableCount = useMemo(() => agents.filter((agent) => agent.ownsLeads).length, [agents]);

	// Columns toggleable via the Columns dropdown (per Figma): everything except the always-on
	// Member ID, Last Login, and Agent Bio columns. AVATAR ("User Avatar") is toggleable.
	const columnOptions = useMemo(
		() => defaultColumns.filter(
			(c) => c !== AgentColumnType.ID && c !== AgentColumnType.LAST_LOGIN && c !== AgentColumnType.ACTION
		),
		[defaultColumns]
	);

	// Search (name/email) + role/routing filters applied before the table.
	const filteredAgents = useMemo(() => {
		const lowerTerm = term.trim().toLowerCase();
		return agents.filter((agent) => {
			if (lowerTerm) {
				// Match name, email, or member ID (the Figma search is "Search users").
				const haystack = `${agent.name} ${agent.email ?? ""} ${agent.id}`.toLowerCase();
				if (!haystack.includes(lowerTerm)) {
					return false;
				}
			}
			if (filters.roles.length) {
				const roleTags = getRoleTags(agent);
				if (!filters.roles.some((role) => roleTags.includes(role))) {
					return false;
				}
			}
			if (filters.routing.length) {
				const routingTags = getRoutingTags(agent, roundRobinRoutes, geographicRoutingRoutes);
				if (!filters.routing.some((routing) => routingTags.includes(routing))) {
					return false;
				}
			}
			if (filters.offices.length) {
				const officeId = agent.office?.id;
				if (!officeId || !filters.offices.includes(officeId)) {
					return false;
				}
			}
			if (!matchesBillableFilter(agent, filters.billable)) {
				return false;
			}
			return true;
		});
	}, [agents, term, filters, roundRobinRoutes, geographicRoutingRoutes]);

	const bioUrlFor = useCallback((agent: Agent) => {
		return agent.hasOwnSite ? (agent.redirectUrl || agent.agentPageUrl) : agent.agentPageUrl;
	}, []);

	// Member ID, Last Login, and Agent Bio are always shown (not in the Columns toggle, per Figma);
	// everything else is governed by the persisted visible-column selection.
	const alwaysVisible = useMemo(
		() => [AgentColumnType.ID, AgentColumnType.LAST_LOGIN, AgentColumnType.ACTION],
		[]
	);
	const isVisible = useCallback(
		(type: AgentColumnType) => alwaysVisible.includes(type) || columnTypesValues.includes(type),
		[columnTypesValues, alwaysVisible]
	);

	const columns = [
		{
			id: "avatar",
			hidden: !isVisible(AgentColumnType.AVATAR),
			width: 70,
			value: (agent: Agent) => (
				<Mui.Link
					className={classes.centeredCell}
					component={RouterLinkWrapper}
					to={getMemberUrl(agent, user.agentId)}
				>
					<Avatar phrase={agent.label} src={agent.photoUrl} color={theme.palette.primary.main} />
				</Mui.Link>
			),
		},
		{
			id: "label",
			hidden: !isVisible(AgentColumnType.LABEL),
			title: AgentColumnType.LABEL.label,
			sort: (a: Agent, b: Agent) =>
				(a.lastName || "").toLowerCase() < (b.lastName || "").toLowerCase() ? -1 : 1,
			value: (agent: Agent) => (
				<Mui.Grid container direction="column">
					<Mui.Grid item>
						<Mui.Link
							className={classes.link}
							component={RouterLinkWrapper}
							to={getMemberUrl(agent, user.agentId)}
						>
							{getMemberDisplayName(agent, user.agentId)}
						</Mui.Link>
					</Mui.Grid>
					<Mui.Grid item>
						<Mui.Link href={`mailto:${agent.email}`} className={classes.email}>
							{agent.email}
						</Mui.Link>
					</Mui.Grid>
				</Mui.Grid>
			),
		},
		{
			id: AgentColumnType.ID.id,
			hidden: !isVisible(AgentColumnType.ID),
			title: AgentColumnType.ID.label,
			value: (agent: Agent) => <Mui.Typography>{agent.id}</Mui.Typography>,
		},
		{
			id: AgentColumnType.ROLE.id,
			hidden: !isVisible(AgentColumnType.ROLE),
			title: AgentColumnType.ROLE.label,
			sort: (a: Agent, b: Agent) =>
				getRoleTags(a).join(",").localeCompare(getRoleTags(b).join(",")),
			value: (agent: Agent) => {
				const tags = getRoleTags(agent);
				if (!tags.length) {
					return null;
				}
				return (
					<div className={classes.tagStack}>
						{tags.map((tag) => (
							<Tag key={tag} label={ROLE_TAG_LABELS[tag]} variant={tag === "Admin" ? "primary" : "brand"} />
						))}
					</div>
				);
			},
		},
		{
			id: AgentColumnType.BILLABLE_USER.id,
			hidden: !isVisible(AgentColumnType.BILLABLE_USER),
			title: `${AgentColumnType.BILLABLE_USER.label} (${billableCount})`,
			value: (agent: Agent) => (agent.ownsLeads ? <Icons.Check className={classes.billableCheck} /> : null),
		},
		{
			id: AgentColumnType.LEADS.id,
			hidden: !isVisible(AgentColumnType.LEADS),
			title: AgentColumnType.LEADS.label,
			sort: (a: Agent, b: Agent) => leadCountFor(a) - leadCountFor(b),
			value: (agent: Agent) => {
				const count = leadCountFor(agent);
				return count ? count : null;
			},
		},
		{
			id: AgentColumnType.ACTIVE_LISTINGS.id,
			hidden: !isVisible(AgentColumnType.ACTIVE_LISTINGS),
			title: AgentColumnType.ACTIVE_LISTINGS.label,
			sort: (a: Agent, b: Agent) => (a.activeListingsCount ?? 0) - (b.activeListingsCount ?? 0),
			value: (agent: Agent) => agent.activeListingsCount ?? 0,
		},
		{
			id: AgentColumnType.OFFICE.id,
			hidden: !isVisible(AgentColumnType.OFFICE),
			title: AgentColumnType.OFFICE.label,
			sort: (a: Agent, b: Agent) =>
				(a.office?.name || "").toLowerCase().localeCompare((b.office?.name || "").toLowerCase()),
			value: (agent: Agent) => agent.office?.name ?? "",
		},
		{
			id: AgentColumnType.ROUTING.id,
			hidden: !isVisible(AgentColumnType.ROUTING),
			width: 180,
			title: AgentColumnType.ROUTING.label,
			value: (agent: Agent) => {
				const tags = getRoutingTags(agent, roundRobinRoutes, geographicRoutingRoutes);
				if (!tags.length) {
					return null;
				}
				return (
					<div className={classes.tagStack}>
						{tags.map((tag) => (
							<Tag
								key={tag}
								label={tag}
								variant="neutral"
								icon={tag === "Round Robin" ? <Icons.RotateCw size={14} /> : <Icons.MapPin size={14} />}
							/>
						))}
					</div>
				);
			},
		},
		{
			id: AgentColumnType.LAST_LOGIN.id,
			hidden: !isVisible(AgentColumnType.LAST_LOGIN),
			title: AgentColumnType.LAST_LOGIN.label,
			sort: (a: Agent, b: Agent) => {
				const aTime = a.lastLoginDate ? a.lastLoginDate.valueOf() : 0;
				const bTime = b.lastLoginDate ? b.lastLoginDate.valueOf() : 0;
				return aTime - bTime;
			},
			value: (agent: Agent) => {
				if (!agent.lastLoginDate) {
					return null;
				}
				return numberedDateFormatter(moment(agent.lastLoginDate));
			},
		},
		{
			id: AgentColumnType.ACTION.id,
			hidden: !isVisible(AgentColumnType.ACTION),
			title: AgentColumnType.ACTION.label,
			width: 160,
			value: (agent: Agent) => {
				const url = bioUrlFor(agent);
				return url ? (
					<Mui.Link
						href={url}
						target="_blank"
						rel="noopener"
						className={classes.bioLink}
					>
						View Agent Bio
						<Icons.ExternalLink size={16} />
					</Mui.Link>
				) : null;
			},
		},
	];

	const dialogs = (
		<>
			{addAgentDialogIsOpen && (
				<AddMemberDialog
					agents={agents}
					open={addAgentDialogIsOpen}
					onClose={hideAddAgentDialog}
				/>
			)}
		</>
	);

	const toolbar = (
		<TeamToolbar
			term={term}
			onTermChange={setTerm}
			sortOptions={SORT_OPTIONS}
			sort={sort}
			onSortChange={setSort}
			filters={filters}
			onFiltersChange={setFilters}
			officeOptions={officeOptions}
			columnOptions={columnOptions}
			visibleColumns={columnTypesValues.filter((type) => columnOptions.includes(type))}
			onColumnsChange={handleColumnsChange}
			showColumnControls={!!user.permissions.showTeamFeatures}
			onAddMember={showAddAgentDialog}
		/>
	);

	// MAX-4128: agents.length, not showTeamFeatures — that flag is agentCount > 1, so it hid the solo admin.
	if (!agents.length) {
		return (
			<>
				{dialogs}
				<Mui.Grid container direction="column" spacing={4}>
					<Mui.Grid item>{toolbar}</Mui.Grid>
					<Mui.Grid item>
						<Mui.Grid container direction="column" alignItems="center" spacing={3} className={classes.emptyState}>
							<Mui.Grid item>
								<Mui.Typography variant="h5" align="center">No team members yet</Mui.Typography>
							</Mui.Grid>
							<Mui.Grid item>
								<Mui.Typography variant="body1" align="center" color="textSecondary">
									Add your first team member to get started
								</Mui.Typography>
							</Mui.Grid>
						</Mui.Grid>
					</Mui.Grid>
				</Mui.Grid>
			</>
		);
	}

	// Mobile: card layout instead of the horizontal table.
	if (isMobile) {
		const sortedMobile = [...filteredAgents];
		const sortColumn = columns.find((c) => c.id === sort.id && c.sort);
		if (sortColumn && sortColumn.sort) {
			sortedMobile.sort((a, b) => sortColumn.sort!(a, b) * (sort.desc ? -1 : 1));
		}
		return (
			<>
				{dialogs}
				<Mui.Grid container direction="column" spacing={3}>
					<Mui.Grid item>{toolbar}</Mui.Grid>
					<Mui.Grid item>
						{sortedMobile.map((agent) => (
							<MemberCard
								key={agent.id}
								agent={agent}
								displayName={getMemberDisplayName(agent, user.agentId)}
								roleTags={getRoleTags(agent)}
								routingTags={getRoutingTags(agent, roundRobinRoutes, geographicRoutingRoutes)}
								leadCount={leadCountFor(agent)}
								billable={agent.ownsLeads}
								lastLogin={agent.lastLoginDate ? numberedDateFormatter(moment(agent.lastLoginDate)) : null}
								bioUrl={bioUrlFor(agent)}
							/>
						))}
					</Mui.Grid>
				</Mui.Grid>
			</>
		);
	}

	return (
		<>
			{dialogs}
			<div className={classes.tableToolbar}>{toolbar}</div>
			<RefreshTable
				items={filteredAgents}
				columns={columns}
				sort={sort}
				rowKey={(agent) => agent.id}
				userId={user.agentId}
				tableKey="team"
			/>
		</>
	);
};

export const AgentTable = withMediaQuery("(max-width:600px)")(
	withStyles(AgentTableComponent, styles)
);
